// src/store/authStore.ts
import { create } from 'zustand';
import apiClient from '@/lib/api-client';
import { getMyPermissions, getMyPosPermissions } from '@/features/settings/services/settingsApi';

// ============================================================
// TYPES
// ============================================================

interface User {
    id: string;
    tenantId: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string | null;
    avatarUrl: string | null;
    isActive: boolean;
    posAccess: 'none' | 'retail' | 'bom' | 'both';
    lastLoginAt: string | null;
    createdAt: string;
    updatedAt: string;
}

interface Role {
    id: string;
    name: string;
}

interface PermissionOverride {
    id: string;
    module: string;
    action: string;
    description: string | null;
    granted: boolean;
    reason: string | null;
}

interface BusinessInfo {
    id: string;
    name: string;
    shortCode: string;
    type: string;
    scope?: string;
    branchCount?: number;
    staffCount?: number;
}

interface BranchInfo {
    id: string;
    name: string;
    code: string;
    isMainBranch?: boolean;
}

export interface TenantInfo {
    id: string;
    name: string;
    slug: string;
    businessModel: 'single' | 'multi';
    isSubscribed: boolean;
    subscriptionExpiresAt: string | null;
}

interface LoginResponse {
    user: User;
    roles: Role[];
    tokens: {
        accessToken: string;
        refreshToken: string;
    };
    tenant?: TenantInfo | null;
    businesses?: BusinessInfo[];
    branches?: BranchInfo[];
    selectedBusinessId?: string | null;
    selectedBranchId?: string | null;
}

interface MeResponse {
    user: User;
    roles: Role[];
    tenant?: TenantInfo | null;
}

interface MyPermissionsResponse {
    permissions: string[];
    roles: Role[];
    overrides: PermissionOverride[];
}

interface AuthState {
    user: User | null;
    tenant: TenantInfo | null;
    roles: Role[];
    permissions: string[];
    permissionOverrides: PermissionOverride[];
    isAuthenticated: boolean;
    isLoading: boolean;

    // Business management
    businesses: BusinessInfo[];
    selectedBusiness: BusinessInfo | null;
    selectedBusinessId: string | null;

    // Branch management
    branches: BranchInfo[];
    selectedBranch: BranchInfo | null;
    selectedBranchId: string | null;

    // Actions
    login: (email: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
    fetchUser: () => Promise<void>;
    fetchMyPermissions: () => Promise<void>;
    fetchMyBusinesses: () => Promise<void>;
    fetchMyBranches: () => Promise<void>;
    selectBusiness: (businessId: string) => Promise<void>;
    selectBranch: (branchId: string) => Promise<void>;
    setUser: (user: User, roles: Role[]) => void;
    setTenant: (tenant: TenantInfo | null) => void;
    updateTenantSubscription: (isSubscribed: boolean) => void;
    clearAuth: () => void;

    // Helpers
    hasPermission: (permissionKey: string) => boolean;
    hasAnyPermission: (permissionKeys: string[]) => boolean;
    hasAllPermissions: (permissionKeys: string[]) => boolean;
    hasRole: (roleName: string) => boolean;
    hasAnyRole: (roleNames: string[]) => boolean;
}

// Helper function to extract posAccess from JWT token
function getPosAccessFromJwt(): 'none' | 'retail' | 'bom' | 'both' {
    try {
        const token = localStorage.getItem('accessToken');
        if (token) {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const posAccess = (payload.posAccess || 'none').toLowerCase();
            if (['none', 'retail', 'bom', 'both'].includes(posAccess)) {
                return posAccess as 'none' | 'retail' | 'bom' | 'both';
            }
        }
    } catch { }
    return 'none';
}

// ============================================================
// STORE
// ============================================================

export const useAuthStore = create<AuthState>((set, get) => ({
    user: null,
    tenant: null,
    roles: [],
    permissions: [],
    permissionOverrides: [],
    isAuthenticated: !!localStorage.getItem('accessToken'),
    isLoading: false,

    // Business state
    businesses: [],
    selectedBusiness: null,
    selectedBusinessId: localStorage.getItem('selectedBusinessId') || null,

    // Branch state
    branches: [],
    selectedBranch: null,
    selectedBranchId: localStorage.getItem('selectedBranchId') || null,

    // ----------------------------------------------------------
    // LOGIN
    // ----------------------------------------------------------
    login: async (email: string, password: string) => {
        set({ isLoading: true });
        try {
            const { data } = await apiClient.post<{
                success: true;
                data: LoginResponse;
            }>('/auth/login', { email, password });

            const responseData = data.data;

            localStorage.setItem('accessToken', responseData.tokens.accessToken);
            localStorage.setItem('refreshToken', responseData.tokens.refreshToken);

            // Set selected IDs from login response
            if (responseData.selectedBusinessId) {
                localStorage.setItem('selectedBusinessId', responseData.selectedBusinessId);
            }
            if (responseData.selectedBranchId) {
                localStorage.setItem('selectedBranchId', responseData.selectedBranchId);
            }

            // ✅ FIX: Determine posAccess from multiple sources
            let posAccess: 'none' | 'retail' | 'bom' | 'both' = responseData.user?.posAccess || 'none';

            // First try from JWT (most reliable since it's signed)
            const jwtPosAccess = getPosAccessFromJwt();
            if (jwtPosAccess !== 'none' || !posAccess || posAccess === 'none') {
                posAccess = jwtPosAccess;
            }

            // If still none, try the dedicated endpoint
            if (posAccess === 'none') {
                try {
                    posAccess = await getMyPosPermissions();
                } catch (err) {
                    console.warn('[authStore] Failed to fetch posAccess from endpoint');
                }
            }

            // Update user object with normalized posAccess
            const userWithPosAccess: User = {
                ...responseData.user,
                posAccess: posAccess,
            };

            set({
                user: userWithPosAccess,
                tenant: responseData.tenant || null,
                roles: responseData.roles,
                businesses: responseData.businesses || [],
                branches: responseData.branches || [],
                selectedBusinessId: responseData.selectedBusinessId || null,
                selectedBranchId: responseData.selectedBranchId || null,
                selectedBusiness: responseData.businesses?.find(
                    (b) => b.id === responseData.selectedBusinessId
                ) || null,
                selectedBranch: responseData.branches?.find(
                    (b) => b.id === responseData.selectedBranchId
                ) || null,
                isAuthenticated: true,
                isLoading: false,
            });

            // Fetch permissions in background
            get().fetchMyPermissions();
        } catch (error) {
            set({ isLoading: false });
            throw error;
        }
    },

    // ----------------------------------------------------------
    // LOGOUT
    // ----------------------------------------------------------
    logout: async () => {
        const refreshToken = localStorage.getItem('refreshToken');

        try {
            await apiClient.post('/auth/logout', {
                refreshToken: refreshToken || undefined,
            });
        } catch {
            // Ignore errors
        }

        get().clearAuth();
    },

    // ----------------------------------------------------------
    // FETCH CURRENT USER
    // ----------------------------------------------------------
    fetchUser: async () => {
        try {
            const { data } = await apiClient.get<{
                success: true;
                data: MeResponse;
            }>('/auth/me');

            // ✅ FIX: Determine posAccess from multiple sources
            let posAccess: 'none' | 'retail' | 'bom' | 'both' = data.data.user?.posAccess || 'none';

            // Try from JWT first
            const jwtPosAccess = getPosAccessFromJwt();
            if (jwtPosAccess !== 'none' || !posAccess || posAccess === 'none') {
                posAccess = jwtPosAccess;
            }

            // If still none, try the dedicated endpoint
            if (posAccess === 'none') {
                try {
                    posAccess = await getMyPosPermissions();
                } catch {
                    console.warn('[authStore] Failed to fetch posAccess from endpoint');
                }
            }

            set({
                user: {
                    ...data.data.user,
                    posAccess: posAccess,
                },
                tenant: data.data.tenant || null,
                roles: data.data.roles,
                isAuthenticated: true,
            });

            get().fetchMyPermissions();
            get().fetchMyBusinesses();
            get().fetchMyBranches();
        } catch {
            get().clearAuth();
        }
    },

    // ----------------------------------------------------------
    // FETCH MY PERMISSIONS
    // ----------------------------------------------------------
    fetchMyPermissions: async () => {
        try {
            const data: MyPermissionsResponse = await getMyPermissions();
            set({
                permissions: data.permissions || [],
                roles: data.roles || get().roles,
                permissionOverrides: data.overrides || [],
            });
        } catch {
            console.warn('[authStore] Failed to fetch permissions');
        }
    },

    // ----------------------------------------------------------
    // FETCH MY BUSINESSES
    // ----------------------------------------------------------
    fetchMyBusinesses: async () => {
        try {
            const { data } = await apiClient.get('/auth/me/businesses');
            const businesses = (data.data || []) as BusinessInfo[];

            set({ businesses });

            // Auto-select if only one business and none selected
            if (businesses.length === 1 && !get().selectedBusinessId) {
                await get().selectBusiness(businesses[0].id);
            }
        } catch {
            console.warn('[authStore] Failed to fetch businesses');
        }
    },

    // ----------------------------------------------------------
    // FETCH MY BRANCHES
    // ----------------------------------------------------------
    fetchMyBranches: async () => {
        try {
            const { data } = await apiClient.get('/auth/me/branches');
            const branches = data.data || [];

            const normalized: BranchInfo[] = branches.map((b: any) => ({
                id: b.id || b.branchId,
                name: b.name || b.branchName || 'Unknown',
                code: b.code || '',
                isMainBranch: b.isMainBranch || false,
            }));

            set({ branches: normalized });

            // Auto-select if only one branch and none selected
            if (normalized.length === 1 && !get().selectedBranchId) {
                await get().selectBranch(normalized[0].id);
            }
        } catch {
            console.warn('[authStore] Failed to fetch branches');
        }
    },

    // ----------------------------------------------------------
    // SELECT BUSINESS
    // ----------------------------------------------------------
    selectBusiness: async (businessId: string) => {
        try {
            const { data } = await apiClient.post('/auth/select-business', { businessId });

            const newToken = data.data?.accessToken;
            if (newToken) {
                localStorage.setItem('accessToken', newToken);
            }
            localStorage.setItem('selectedBusinessId', businessId);

            const business = get().businesses.find((b) => b.id === businessId) || null;

            // Update branches from the response
            const newBranches: BranchInfo[] = (data.data?.branches || []).map((b: any) => ({
                id: b.id,
                name: b.name || 'Unknown',
                code: b.code || '',
                isMainBranch: b.isMainBranch || false,
            }));

            const newSelectedBranchId = data.data?.selectedBranchId || null;
            if (newSelectedBranchId) {
                localStorage.setItem('selectedBranchId', newSelectedBranchId);
            }

            const newSelectedBranch = newBranches.find(
                (b: BranchInfo) => b.id === newSelectedBranchId
            ) || null;

            // ✅ FIX: Update posAccess from new token
            const posAccess = getPosAccessFromJwt();

            set({
                selectedBusiness: business,
                selectedBusinessId: businessId,
                branches: newBranches,
                selectedBranch: newSelectedBranch,
                selectedBranchId: newSelectedBranchId,
                user: get().user ? { ...get().user!, posAccess } : null,
            });

            await get().fetchMyPermissions();
        } catch (error) {
            console.error('[authStore] Failed to select business:', error);
            throw error;
        }
    },

    // ----------------------------------------------------------
    // SELECT BRANCH
    // ----------------------------------------------------------
    selectBranch: async (branchId: string) => {
        try {
            const { data } = await apiClient.post('/auth/select-branch', { branchId });

            const newToken = data.data?.accessToken;
            if (newToken) {
                localStorage.setItem('accessToken', newToken);
            }
            localStorage.setItem('selectedBranchId', branchId);

            const branch = get().branches.find((b) => b.id === branchId) || null;

            // ✅ FIX: Update posAccess from new token
            const posAccess = getPosAccessFromJwt();

            set({
                selectedBranch: branch,
                selectedBranchId: branchId,
                user: get().user ? { ...get().user!, posAccess } : null,
            });

            await get().fetchMyPermissions();
        } catch (error) {
            console.error('[authStore] Failed to select branch:', error);
            throw error;
        }
    },

    // ----------------------------------------------------------
    // MANUAL SET
    // ----------------------------------------------------------
    setUser: (user: User, roles: Role[]) => {
        set({ user, roles, isAuthenticated: true });
        get().fetchMyPermissions();
        get().fetchMyBusinesses();
        get().fetchMyBranches();
    },

    setTenant: (tenant: TenantInfo | null) => {
        set({ tenant });
    },

    updateTenantSubscription: (isSubscribed: boolean) => {
        set((state) => ({
            tenant: state.tenant
                ? { ...state.tenant, isSubscribed }
                : {
                    id: state.user?.tenantId || localStorage.getItem('tenantId') || '',
                    name: 'My Organization',
                    slug: '',
                    businessModel: 'single',
                    isSubscribed,
                    subscriptionExpiresAt: null,
                },
        }));
    },

    // ----------------------------------------------------------
    // MANUAL CLEAR
    // ----------------------------------------------------------
    clearAuth: () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('selectedBusinessId');
        localStorage.removeItem('selectedBranchId');
        set({
            user: null,
            tenant: null,
            roles: [],
            permissions: [],
            permissionOverrides: [],
            businesses: [],
            selectedBusiness: null,
            selectedBusinessId: null,
            branches: [],
            selectedBranch: null,
            selectedBranchId: null,
            isAuthenticated: false,
        });
    },

    // ----------------------------------------------------------
    // PERMISSION HELPERS
    // ----------------------------------------------------------
    hasPermission: (permissionKey: string) => {
        if (get().roles.some((r) => r.name === 'Super Admin')) return true;
        return get().permissions.includes(permissionKey);
    },

    hasAnyPermission: (permissionKeys: string[]) => {
        if (get().roles.some((r) => r.name === 'Super Admin')) return true;
        return permissionKeys.some((key) => get().permissions.includes(key));
    },

    hasAllPermissions: (permissionKeys: string[]) => {
        if (get().roles.some((r) => r.name === 'Super Admin')) return true;
        return permissionKeys.every((key) => get().permissions.includes(key));
    },

    // ----------------------------------------------------------
    // ROLE HELPERS
    // ----------------------------------------------------------
    hasRole: (roleName: string) => {
        return get().roles.some((r) => r.name === roleName);
    },

    hasAnyRole: (roleNames: string[]) => {
        const userRoleNames = get().roles.map((r) => r.name);
        return roleNames.some((name) => userRoleNames.includes(name));
    },
}));